import { useState } from 'react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import {
  Button,
  Input,
  Spinner,
  UncontrolledAccordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
} from 'reactstrap';
import { ChevronDown, Filter, X } from 'react-feather';
import { getCamelCaseWord } from '../static/brands_wholesalers_data';

// Accordion sections for the brands/retailers table filters. Keys a table doesn't
// return are skipped; keys not listed here land in "Other", so a new backend
// filter still shows up.
const FILTER_GROUPS = [
  {
    id: 'attributes',
    title: 'Attributes',
    filters: { categories: 'Categories', 'target group': 'Target Group', location: 'Location', style: 'Style', price: 'Price' },
  },
  {
    id: 'funnel',
    title: 'Data Quality Funnel',
    filters: {
      website_status_group: 'Website Status',
      summary_status: 'Summary',
      fashion_classification: 'Fashion Classification',
      entity_group: 'Entity',
    },
  },
  {
    id: 'completeness',
    title: 'Profile Completeness',
    filters: { profile_status: 'Profile Status', only_missing_attribute: 'Only Missing Attribute', aggregate: 'Aggregated Attributes' },
  },
  {
    id: 'matching',
    title: 'Matching & Relationships',
    filters: {
      match_status: 'Match Status',
      competitor_status: 'Next to / Competitor',
      working_with_status: 'Working With',
      working_with_profile_status: 'Profile + Working With',
      algorithm: 'Matching Algorithm',
    },
  },
  {
    id: 'source',
    title: 'Source & Classification',
    filters: {
      source: 'Source',
      departmental_store: 'Departmental Store',
      summary_type: 'Fashion Type (summary.type)',
      website_status: 'Website Valid / Invalid',
      entity: 'Entity (exact value)',
    },
  },
];

// The list APIs only read the first value of these keys, so they are pick-one.
const SINGLE_SELECT = new Set([
  'sort_by',
  'location',
  'profile_status',
  'match_status',
  'competitor_status',
  'working_with_status',
  'working_with_profile_status',
  'summary_type',
  'algorithm',
]);

const SEARCH_FROM = 20; // show a search box for lists longer than this
const INK = '#2A2F38';
const MUTED = '#6B7280';
const BORDER = '#E5E7EB';

const fmt = (n) => (n == null || n === '' ? '' : Number(n).toLocaleString());
const countOf = (filters, keys) =>
  keys.reduce((sum, k) => sum + (Array.isArray(filters?.[k]) ? filters[k].length : 0), 0);
// Drop emptied keys so "no filters" really is no filters; keep non-list keys
// (e.g. date ranges set by the dashboard).
const clean = (filters) =>
  Object.fromEntries(Object.entries(filters).filter(([, v]) => !Array.isArray(v) || v.length));

const chipStyle = (selected) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '5px 10px',
  borderRadius: '16px',
  border: `1px solid ${selected ? '#2e99f2' : BORDER}`,
  background: selected ? '#E3F2FD' : '#F9FAFB',
  color: INK,
  fontSize: '12px',
  fontWeight: selected ? 600 : 400,
  cursor: 'pointer',
});

const FilterOptions = ({ options, selected, onToggle }) => {
  const [query, setQuery] = useState('');
  const shown = query
    ? options.filter((o) => String(o.name ?? '').toLowerCase().includes(query.toLowerCase()))
    : options;

  return (
    <>
      {options.length > SEARCH_FROM && (
        <Input bsSize="sm" className="mb-2" placeholder={`Search ${options.length} options...`} value={query} onChange={(e) => setQuery(e.target.value)} />
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '196px', overflowY: 'auto' }}>
        {shown.map((option) => {
          const isSelected = selected.includes(option.name);
          return (
            <button type="button" key={option.name} aria-pressed={isSelected} onClick={() => onToggle(option.name)} style={chipStyle(isSelected)}>
              <span>{option.name}</span>
              {option.count != null && <span style={{ color: MUTED }}>{fmt(option.count)}</span>}
            </button>
          );
        })}
        {!shown.length && <span style={{ fontSize: '12px', color: MUTED }}>No matches</span>}
      </div>
    </>
  );
};

const FilterDrawer = ({ data = {}, filters = {}, isLoading, onFilterApply }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(filters);

  const openDrawer = () => {
    setDraft(filters);
    setOpen(true);
  };
  const apply = () => {
    onFilterApply(clean(draft));
    setOpen(false);
  };

  const toggle = (key, value) =>
    setDraft((prev) => {
      const current = prev[key] || [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : SINGLE_SELECT.has(key) ? [value] : [...current, value];
      return { ...prev, [key]: next };
    });

  // A selected value missing from the options (e.g. set by a dashboard link) is
  // still listed, so it can be seen and removed.
  const optionsFor = (key) => {
    const options = data[key] || [];
    const extra = (draft[key] || []).filter((v) => !options.some((o) => o.name === v)).map((name) => ({ name }));
    return [...options, ...extra];
  };

  const keys = Object.keys(data).filter((k) => k !== 'sort_by' && optionsFor(k).length);
  const known = new Set(FILTER_GROUPS.flatMap((g) => Object.keys(g.filters)));
  const groups = [
    ...FILTER_GROUPS.map((g) => ({ ...g, keys: Object.keys(g.filters).filter((k) => keys.includes(k)) })),
    { id: 'other', title: 'Other', filters: {}, keys: keys.filter((k) => !known.has(k)) },
  ].filter((g) => g.keys.length);

  const filterKeys = Object.keys({ ...data, ...filters }).filter((k) => k !== 'sort_by');
  const appliedCount = countOf(filters, filterKeys);
  const draftCount = countOf(draft, filterKeys);
  // Open the sections that have something selected, or the first one if none do.
  const withSelection = groups.filter((g) => countOf(draft, g.keys)).map((g) => g.id);
  const defaultOpen = withSelection.length ? withSelection : groups.slice(0, 1).map((g) => g.id);

  return (
    <>
      <Button
        color="link"
        onClick={openDrawer}
        aria-label={`Filters${appliedCount ? ` (${appliedCount} active)` : ''}`}
        style={{ borderRadius: '6px', backgroundColor: '#E0E0E0', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Filter color="#fff" size={20} />
        {appliedCount > 0 && (
          <span
            style={{
              position: 'absolute', top: '-7px', right: '-7px', minWidth: '18px', height: '18px', padding: '0 5px',
              borderRadius: '9px', background: '#2e99f2', color: '#fff', fontSize: '11px', fontWeight: 700, lineHeight: '18px', textAlign: 'center',
            }}
          >
            {appliedCount}
          </span>
        )}
      </Button>

      <Drawer open={open} onClose={() => setOpen(false)} direction="right" size="min(480px, 100vw)" zIndex={1040}>
        {open && (
          <div className="filter-drawer" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* The app theme hides Bootstrap's accordion arrow globally; draw our own, scoped here. */}
            <style>{`
              .filter-drawer .filter-drawer-chevron { transition: transform 0.2s ease; color: ${MUTED}; flex-shrink: 0; }
              .filter-drawer .accordion-button:not(.collapsed) .filter-drawer-chevron { transform: rotate(180deg); }
            `}</style>
            <div className="d-flex justify-content-between align-items-center px-3 py-3" style={{ borderBottom: `1px solid ${BORDER}` }}>
              <div>
                <h5 className="mb-0 fw-bold" style={{ color: INK }}>Filters</h5>
                <span style={{ fontSize: '12px', color: MUTED }}>{draftCount ? `${draftCount} selected` : 'Nothing selected'}</span>
              </div>
              <button type="button" className="btn p-1" onClick={() => setOpen(false)} aria-label="Close filters">
                <X size={20} />
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }} className="px-3 py-3">
              {isLoading ? (
                <div className="d-flex justify-content-center py-5"><Spinner color="dark" /></div>
              ) : (
                <>
                  {data.sort_by?.length > 0 && (
                    <div className="mb-3">
                      <div style={{ fontSize: '13px', fontWeight: 600, color: INK, marginBottom: '6px' }}>Sort by</div>
                      <div className="d-flex" style={{ gap: '6px' }}>
                        {data.sort_by.map((option) => {
                          const isSelected = draft.sort_by?.includes(option.name);
                          return (
                            <button type="button" key={option.name} aria-pressed={isSelected} onClick={() => toggle('sort_by', option.name)} style={chipStyle(isSelected)}>
                              {option.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {groups.length ? (
                    <UncontrolledAccordion stayOpen defaultOpen={defaultOpen}>
                      {groups.map((group) => {
                        const groupCount = countOf(draft, group.keys);
                        return (
                          <AccordionItem key={group.id}>
                            <AccordionHeader targetId={group.id}>
                              <span style={{ fontWeight: 600, color: INK }}>{group.title}</span>
                              {groupCount > 0 && (
                                <span className="ms-2" style={{ fontSize: '11px', fontWeight: 700, color: '#fff', background: '#2e99f2', borderRadius: '9px', padding: '1px 7px' }}>
                                  {groupCount}
                                </span>
                              )}
                              <ChevronDown size={18} className="ms-auto filter-drawer-chevron" aria-hidden="true" />
                            </AccordionHeader>
                            <AccordionBody accordionId={group.id}>
                              {group.keys.map((key) => (
                                <div key={key} className="mb-3">
                                  <div style={{ fontSize: '13px', fontWeight: 600, color: INK, marginBottom: '6px' }}>
                                    {group.filters[key] || getCamelCaseWord(key)}
                                    {SINGLE_SELECT.has(key) && <span style={{ fontWeight: 400, color: MUTED }}> · pick one</span>}
                                  </div>
                                  <FilterOptions options={optionsFor(key)} selected={draft[key] || []} onToggle={(value) => toggle(key, value)} />
                                </div>
                              ))}
                            </AccordionBody>
                          </AccordionItem>
                        );
                      })}
                    </UncontrolledAccordion>
                  ) : (
                    <div className="text-center text-muted py-4">No filters available</div>
                  )}
                </>
              )}
            </div>

            <div className="d-flex justify-content-between align-items-center px-3 py-3" style={{ borderTop: `1px solid ${BORDER}` }}>
              <Button color="link" className="p-0" disabled={!draftCount && !draft.sort_by?.length} onClick={() => setDraft({})}>
                Clear all
              </Button>
              <div className="d-flex" style={{ gap: '8px' }}>
                <Button color="primary" outline onClick={() => setOpen(false)}>Cancel</Button>
                <Button color="primary" onClick={apply}>Apply{draftCount ? ` (${draftCount})` : ''}</Button>
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </>
  );
};

export default FilterDrawer;
